import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Plus,
  Users,
  Search,
  Tag,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Check,
  Upload,
  Download
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Contact {
  id: string;
  instance_id: string;
  phone_number: string;
  full_name: string;
  tags: string[];
  created_at: string;
}

interface Instance {
  id: string;
  name: string;
}

export default function Contacts() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [formData, setFormData] = useState({
    instance_id: instanceId || '',
    phone_number: '',
    full_name: '',
    tags: [] as string[],
    newTag: ''
  });

  useEffect(() => {
    if (instanceId) {
      fetchContacts(instanceId);
    } else {
      fetchInstances();
      fetchAllContacts();
    }
  }, [instanceId]);

  async function fetchInstances() {
    try {
      const { data, error: instancesError } = await supabase
        .from('instances')
        .select('id, name')
        .eq('is_active', true);

      if (instancesError) throw instancesError;
      setInstances(data || []);
    } catch (err: any) {
      console.error('Error fetching instances:', err);
      setError('Error al cargar las instancias');
    }
  }

  async function fetchAllContacts() {
    try {
      setLoading(true);
      setError('');

      const { data, error: contactsError } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (contactsError) throw contactsError;
      setContacts(data || []);
    } catch (err: any) {
      console.error('Error fetching all contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchContacts(instanceId: string) {
    try {
      setLoading(true);
      setError('');

      const { data, error: contactsError } = await supabase
        .from('contacts')
        .select('*')
        .eq('instance_id', instanceId)
        .order('created_at', { ascending: false });

      if (contactsError) throw contactsError;
      setContacts(data || []);
    } catch (err: any) {
      console.error('Error fetching contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');

      if (!formData.instance_id) {
        setError('Debe seleccionar una instancia');
        return;
      }

      // Validate phone number format
      const phoneRegex = /^\+[1-9]\d{1,14}$/;
      if (!phoneRegex.test(formData.phone_number)) {
        setError('El número de teléfono debe incluir el código de país y seguir el formato E.164 (Ej: +34612345678)');
        return;
      }

      const contactData = {
        instance_id: formData.instance_id,
        phone_number: formData.phone_number,
        full_name: formData.full_name,
        tags: formData.tags
      };

      if (selectedContact) {
        // Update existing contact
        const { error: updateError } = await supabase
          .from('contacts')
          .update(contactData)
          .eq('id', selectedContact.id);

        if (updateError) throw updateError;
      } else {
        // Create new contact
        const { error: insertError } = await supabase
          .from('contacts')
          .insert(contactData);

        if (insertError) throw insertError;
      }

      setShowNewModal(false);
      setShowEditModal(false);
      setSelectedContact(null);
      setFormData({
        instance_id: formData.instance_id,
        phone_number: '',
        full_name: '',
        tags: [],
        newTag: ''
      });

      if (instanceId) {
        await fetchContacts(instanceId);
      } else {
        await fetchAllContacts();
      }

    } catch (err: any) {
      console.error('Error saving contact:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (contactId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este contacto?')) return;

    try {
      setError('');

      const { error: deleteError } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId);

      if (deleteError) throw deleteError;

      if (instanceId) {
        await fetchContacts(instanceId);
      } else {
        await fetchAllContacts();
      }

    } catch (err: any) {
      console.error('Error deleting contact:', err);
      setError(err.message);
    }
  };

  const addTag = () => {
    if (formData.newTag.trim() && !formData.tags.includes(formData.newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, formData.newTag.trim()],
        newTag: ''
      });
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const filteredContacts = contacts.filter(contact =>
    contact.phone_number.includes(searchTerm) ||
    contact.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getInstanceName = (instanceId: string) => {
    const instance = instances.find(i => i.id === instanceId);
    return instance ? instance.name : 'Instancia no encontrada';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver a Canales
            </button>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nuevo Contacto
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  Contactos
                </h2>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar contactos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                <button
                  onClick={() => {/* Implementar importación */}}
                  className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </button>
                <button
                  onClick={() => {/* Implementar exportación */}}
                  className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="px-6 py-4">
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  {!instanceId && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instancia
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Etiquetas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      Cargando contactos...
                    </td>
                  </tr>
                ) : filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay contactos para mostrar
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {contact.full_name || 'Sin nombre'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.phone_number}
                      </td>
                      {!instanceId && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getInstanceName(contact.instance_id)}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {contact.tags?.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setFormData({
                                instance_id: contact.instance_id,
                                phone_number: contact.phone_number,
                                full_name: contact.full_name,
                                tags: contact.tags || [],
                                newTag: ''
                              });
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(contact.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* New/Edit Contact Modal */}
      {(showNewModal || showEditModal) && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {showNewModal ? 'Nuevo Contacto' : 'Editar Contacto'}
                  </h3>
                  <div className="space-y-4">
                    {!instanceId && (
                      <div>
                        <label htmlFor="instance_id" className="block text-sm font-medium text-gray-700">
                          Instancia *
                        </label>
                        <select
                          id="instance_id"
                          value={formData.instance_id}
                          onChange={(e) => setFormData({ ...formData, instance_id: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        >
                          <option value="">Selecciona una instancia</option>
                          {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                              {instance.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
                        Número de Teléfono *
                      </label>
                      <input
                        type="text"
                        id="phone_number"
                        value={formData.phone_number}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        placeholder="Ej: +34612345678"
                        pattern="\+[1-9]\d{1,14}"
                        title="El número debe incluir el código de país (Ej: +34612345678)"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Formato internacional con código de país (Ej: +34612345678)
                      </p>
                    </div>
                    <div>
                      <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Etiquetas
                      </label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="ml-1 text-blue-400 hover:text-blue-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex">
                        <input
                          type="text"
                          value={formData.newTag}
                          onChange={(e) => setFormData({ ...formData, newTag: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                          placeholder="Agregar etiqueta..."
                          className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={addTag}
                          className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {showNewModal ? 'Crear Contacto' : 'Guardar Cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewModal(false);
                      setShowEditModal(false);
                      setSelectedContact(null);
                      setFormData({
                        instance_id: instanceId || '',
                        phone_number: '',
                        full_name: '',
                        tags: [],
                        newTag: ''
                      });
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}